/**
 * OpportunitesDetectees — Vague 4 — Onglet dans Origination.
 *
 * Affiche les opportunités générées par NOAM+Tavily : titre, taille marché,
 * score, fractale, chemin d'accès, persona, connexion annuaire, signal alerte.
 *
 * Pattern : check cached → POST scan/start → polling.
 */
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  RefreshCw,
  Loader2,
  Target,
  AlertTriangle,
  Radio,
  TrendingUp,
  Users,
  Link as LinkIcon,
  ChevronDown,
  ChevronUp,
  Clock,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

function fmtMoney(v, unit) {
  if (v == null) return "—";
  const n = Number(v);
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}Md ${unit || ""}`.trim();
  if (n >= 1e6) return `${(n / 1e6).toFixed(0)}M ${unit || ""}`.trim();
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}k ${unit || ""}`.trim();
  return `${n} ${unit || ""}`.trim();
}

function scoreColor(s) {
  if (s == null) return "#A1A1AA";
  if (s >= 8) return "#1D9E75";
  if (s >= 6) return "#EF9F27";
  if (s >= 4) return "#378ADD";
  return "#A1A1AA";
}

function horizonLabel(h) {
  return { court: "0-6 mois", moyen: "6-24 mois", long: "2-5 ans" }[h] || h || "—";
}

function fractaleColor(f) {
  return { Micro: "#378ADD", "Méso": "#EF9F27", Macro: "#7F77DD" }[f] || "#A1A1AA";
}

export default function OpportunitesDetectees() {
  const { t } = useTranslation();
  const [data, setData] = useState({ opportunities: [], narrative_global: "", scanned_at: null });
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [pushedIdx, setPushedIdx] = useState({});
  const [pushingIdx, setPushingIdx] = useState(null);
  // V23.15.20 — Doctrine Brute/Net : facteur de friction global pour décorer
  // chaque opportunité d'un % net (atteignable réaliste compte tenu des frictions).
  const [globalFactor, setGlobalFactor] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get(`/workspace/origination/opportunities/detected`);
      setData(r.data || { opportunities: [], narrative_global: "", scanned_at: null });
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    refresh();
    // V23.15.20 — Charge le facteur global de friction pour décorer en Net
    api.get("/workspace/valuations/global-friction-factor")
      .then((r) => setGlobalFactor(r.data?.factor ?? 1.0))
      .catch(() => setGlobalFactor(1.0));
  }, [refresh]);

  const triggerScan = async (forceRefresh = false) => {
    setBusy(true);
    setProgress("Démarrage…");    try {
      const startRes = await api.post(`/workspace/origination/opportunities/scan/start`, {
        force_refresh: forceRefresh,
      });
      if (startRes.data?.from_cache && !forceRefresh) {
        await refresh();
        setBusy(false);
        return;
      }
      const jobId = startRes.data?.job_id;
      if (!jobId) throw new Error("no job_id");
      for (let i = 0; i < 45; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const poll = await api.get(`/workspace/origination/opportunities/scan/jobs/${jobId}`);
        setProgress(poll.data?.progress || "");
        if (poll.data?.status === "done") {
          await refresh();
          notify.success(t("saas.origination.opps_done", { defaultValue: "Opportunités détectées générées." }));
          setBusy(false);
          return;
        }
        if (poll.data?.status === "failed") {
          throw new Error(poll.data?.error || "job failed");
        }
      }
      throw new Error("timeout");
    } catch (e) {
      notify.error(fmtError(e, "Scan opportunités échoué"));
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  const pushToPipeline = async (idx) => {
    setPushingIdx(idx);
    try {
      const r = await api.post(`/workspace/origination/opportunities/detected/${idx}/push-to-pipeline`, {
        scan_id: data.scan_id || null,
      });
      const dealId = r.data?.deal_id;
      if (r.data?.deduplicated) {
        setPushedIdx((prev) => ({ ...prev, [idx]: dealId }));
        notify.info(t("saas.origination.opps_dup", { defaultValue: "Déjà présent dans le pipeline — aucun doublon créé." }));
        return;
      }
      const ann = r.data?.annuaire_resolved_count || 0;
      const ticket = r.data?.ticket_mEUR || 0;
      setPushedIdx((prev) => ({ ...prev, [idx]: dealId }));
      notify.success(
        t("saas.origination.opps_pushed", { defaultValue: "Deal créé — visible dans Pipeline (Deal System)" }) +
          ` · ${ticket}M€ · ${ann} entité(s) Annuaire reliée(s)`
      );
    } catch (e) {
      notify.error(fmtError(e, "Création deal échouée"));
    } finally {
      setPushingIdx(null);
    }
  };

  const opps = data.opportunities || [];

  return (
    <div className="space-y-5" data-testid="opps-detectees-root">
      {/* Header + scan button */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="noam-overline inline-flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-[#EF9F27]" />
            {t("saas.origination.opps_title", { defaultValue: "Opportunités détectées" })}
          </div>
          <h2 className="noam-display text-xl font-bold mt-1">
            {t("saas.origination.opps_subtitle", { defaultValue: "Croisement contexte interne × tendances externes" })}
          </h2>
          {data.scanned_at && (
            <div className="text-[11px] text-zinc-500 mt-1 inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {t("saas.origination.opps_scanned_at", { defaultValue: "Généré le" })} {new Date(data.scanned_at).toLocaleString()}
            </div>
          )}
        </div>
        <button
          onClick={() => triggerScan(true)}
          disabled={busy}
          className="btn-primary text-sm"
          data-testid="opps-scan-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {busy ? (progress || t("saas.origination.opps_scanning", { defaultValue: "Analyse…" })) : t("saas.origination.opps_regen", { defaultValue: "Régénérer" })}
        </button>
      </div>

      {opps.length > 0 && (
        <div className="rounded-md border border-[#EF9F27]/30 bg-[#EF9F27]/10 p-3 text-xs text-[#F3B95F]" role="note" data-testid="opp-verification-notice">
          <strong className="block mb-1">Pistes générées — à vérifier</strong>
          Les cartes de cette vue sont des suggestions d’analyse. Les sources, lorsqu’elles sont fournies, sont visibles dans le détail de chaque carte. Vérifiez leur pertinence et leurs preuves avant toute action ; elles ne deviennent pas des deals tant qu’elles ne sont pas envoyées dans le pipeline.
        </div>
      )}

      {/* Narrative global */}
      {data.narrative_global && (
        <div className="noam-card p-4 bg-[#EF9F27]/5 border-[#EF9F27]/20" data-testid="opps-narrative">
          <div className="text-xs text-zinc-200 whitespace-pre-wrap leading-relaxed">{data.narrative_global}</div>
        </div>
      )}

      {/* Empty state */}
      {opps.length === 0 && !busy && (
        <div className="noam-card p-12 text-center" data-testid="opps-empty">
          <Sparkles className="w-12 h-12 text-[#EF9F27] mx-auto opacity-60" />
          <div className="noam-display text-lg mt-4">{t("saas.origination.opps_empty_title", { defaultValue: "Aucune opportunité détectée pour l'instant" })}</div>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            {t("saas.origination.opps_empty_help", { defaultValue: "Lancez une analyse pour générer 4-8 opportunités contextualisées avec votre annuaire, votre pipeline et votre doctrine." })}
          </p>
          <button
            onClick={() => triggerScan(true)}
            className="btn-primary mt-6 mx-auto inline-flex"
            data-testid="opps-first-scan"
          >
            <Sparkles className="w-4 h-4" />
            {t("saas.origination.opps_first_scan", { defaultValue: "Lancer la première analyse" })}
          </button>
        </div>
      )}

      {/* Opportunities list */}
      <div className="space-y-3">
        {opps.map((o, idx) => {
          const isOpen = expandedIdx === idx;
          return (
            <div key={idx} className="noam-card p-5" data-testid={`opp-card-${idx}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span
                      className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                      style={{ background: `${scoreColor(o.score_potentiel)}20`, color: scoreColor(o.score_potentiel), border: `1.5px solid ${scoreColor(o.score_potentiel)}40` }}
                    >
                      {o.score_potentiel ?? "?"}
                    </span>
                    <h3 className="text-sm font-semibold text-white leading-snug">{o.title}</h3>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap text-[10px] mb-2">
                    {o.niveau_fractale && (
                      <span className="px-1.5 py-0.5 rounded border" style={{ borderColor: `${fractaleColor(o.niveau_fractale)}40`, color: fractaleColor(o.niveau_fractale) }}>
                        {o.niveau_fractale}
                      </span>
                    )}
                    <span className="px-1.5 py-0.5 rounded border border-white/10 text-zinc-300 inline-flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {horizonLabel(o.horizon)}
                    </span>
                    {o.taille_marche_local?.value != null && (
                      <span className="px-1.5 py-0.5 rounded border border-white/10 text-[#378ADD]">
                        <TrendingUp className="w-2.5 h-2.5 inline mr-1" />
                        {fmtMoney(o.taille_marche_local.value, o.taille_marche_local.unit)} {o.taille_marche_local.label && `· ${o.taille_marche_local.label}`}
                      </span>
                    )}
                    {o.part_atteignable_pct != null && (
                      <span className="px-1.5 py-0.5 rounded border border-[#1D9E75]/30 text-[#1D9E75]">
                        atteignable brut {o.part_atteignable_pct < 1 ? o.part_atteignable_pct.toFixed(2) : o.part_atteignable_pct.toFixed(1)}%
                      </span>
                    )}
                    {/* V23.15.20 — Net après frictions globales (Brute/Net doctrine) */}
                    {o.part_atteignable_pct != null && globalFactor != null && globalFactor < 1 && (
                      <span className="px-1.5 py-0.5 rounded border border-[#EF9F27]/40 text-[#EF9F27]" data-testid={`opp-${idx}-net-pct`}>
                        net {(o.part_atteignable_pct * globalFactor).toFixed(2)}% <span className="text-zinc-500">×{globalFactor.toFixed(2)}</span>
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-zinc-400 leading-relaxed">{o.description}</p>
                </div>
                <button
                  onClick={() => setExpandedIdx(isOpen ? null : idx)}
                  className="btn-ghost text-[11px] shrink-0"
                  data-testid={`opp-toggle-${idx}`}
                >
                  {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>

              {/* Push to pipeline button */}
              <div className="mt-3 flex items-center justify-end gap-2">
                {pushedIdx[idx] ? (
                  <span className="text-[11px] text-[#1D9E75] inline-flex items-center gap-1.5" data-testid={`opp-pushed-${idx}`}>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {t("saas.origination.opps_pushed_label", { defaultValue: "Ajouté au pipeline" })}
                    <RouterLink to="/app/deal-pipeline" className="underline text-[#378ADD] hover:text-white" data-testid={`opp-view-pipeline-${idx}`}>
                      {t("saas.origination.opps_view_pipeline", { defaultValue: "Voir dans le Pipeline →" })}
                    </RouterLink>
                  </span>
                ) : (
                  <button
                    onClick={() => pushToPipeline(idx)}
                    disabled={pushingIdx === idx}
                    className="btn-ghost text-[11px] text-[#378ADD] hover:text-white border-[#378ADD]/40"
                    data-testid={`opp-push-${idx}`}
                  >
                    {pushingIdx === idx ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />}
                    {t("saas.origination.opps_push", { defaultValue: "Pousser dans le pipeline" })}
                  </button>
                )}
              </div>

              {isOpen && (
                <div className="mt-4 pt-4 border-t border-white/5 space-y-3">
                  {/* Signal d'alerte précoce */}
                  {o.signal_source && (
                    <div className="bg-[#378ADD]/8 border border-[#378ADD]/25 rounded-lg p-3 flex items-start gap-2" data-testid="opp-signal-source">
                      <Radio className="w-4 h-4 text-[#378ADD] shrink-0 mt-0.5" />
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#378ADD] mb-1">Signal déclencheur (croisement)</div>
                        <div className="text-xs text-zinc-200 leading-relaxed">{o.signal_source}</div>
                      </div>
                    </div>
                  )}

                  {o.signal_alerte_precoce && (
                    <div className="bg-[#E24B4A]/8 border border-[#E24B4A]/25 rounded-lg p-3 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-[#E24B4A] shrink-0 mt-0.5" />
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[#E24B4A] mb-1">{t("saas.origination.opps_early_signal", { defaultValue: "Signal d'alerte précoce" })}</div>
                        <div className="text-xs text-zinc-200 leading-relaxed">{o.signal_alerte_precoce}</div>
                      </div>
                    </div>
                  )}

                  {/* Chemin d'accès */}
                  {(o.chemin_acces || []).length > 0 && (
                    <div>
                      <div className="noam-overline mb-2 inline-flex items-center gap-1.5">
                        <Target className="w-3 h-3 text-[#378ADD]" />
                        {t("saas.origination.opps_path", { defaultValue: "Chemin d'accès" })}
                      </div>
                      <ol className="space-y-1.5 text-xs text-zinc-300 list-decimal list-inside">
                        {(o.chemin_acces || []).map((step, i) => (
                          <li key={i} className="leading-relaxed">{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}

                  {/* Persona + Annuaire en grid */}
                  <div className="grid md:grid-cols-2 gap-3">
                    {o.persona_cible && (
                      <div className="bg-white/[0.03] rounded-lg p-3">
                        <div className="noam-overline mb-1.5 inline-flex items-center gap-1">
                          <Users className="w-3 h-3 text-[#7F77DD]" />
                          {t("saas.origination.opps_persona", { defaultValue: "Persona cible" })}
                        </div>
                        <div className="text-xs text-zinc-200">{o.persona_cible}</div>
                      </div>
                    )}
                    {(o.connexion_annuaire || []).length > 0 && (
                      <div className="bg-white/[0.03] rounded-lg p-3">
                        <div className="noam-overline mb-1.5 inline-flex items-center gap-1">
                          <LinkIcon className="w-3 h-3 text-[#1D9E75]" />
                          {t("saas.origination.opps_annuaire", { defaultValue: "Connexion Annuaire" })}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(o.connexion_annuaire || []).map((e, i) => (
                            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-[#1D9E75]/10 text-[#1D9E75] border border-[#1D9E75]/30">
                              {e}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tailles supplémentaires */}
                  {o.taille_marche_mondial?.value != null && (
                    <div className="text-[11px] text-zinc-500">
                      Échelle mondiale : <span className="text-zinc-200">{fmtMoney(o.taille_marche_mondial.value, o.taille_marche_mondial.unit)}</span>
                      {o.taille_marche_mondial.label && ` · ${o.taille_marche_mondial.label}`}
                    </div>
                  )}

                  {/* Sources */}
                  {(o.sources || []).length > 0 && (
                    <div>
                      <div className="noam-overline mb-1">{t("saas.origination.opps_sources", { defaultValue: "Sources" })}</div>
                      <div className="space-y-1">
                        {(o.sources || []).slice(0, 4).map((src, i) => (
                          <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#378ADD] hover:text-white truncate block">
                            {src}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
